import { useState } from 'preact/hooks';
import type { StripeCheckoutSession } from '@stripe/stripe-js';
import type { CampaignPromo, OnsiteProduct, OnsitePrice, CheckoutResponse, TrialStartResponse, CheckoutError, CheckoutCompletionResponse, CheckoutSuccessDetails, TrialDiscount } from '@/types';
import { CheckoutShell } from '@/components/CheckoutShell';
import { OrderSummary } from '@/components/OrderSummary';
import { PaymentForm } from '@/components/PaymentForm';
import { TrialPaymentForm, trialSubtitle } from '@/components/TrialPaymentForm';
import { t, useT } from '@/i18n/t';

interface Props {
  product?: OnsiteProduct;
  price?: OnsitePrice;
  amountCentsOverride?: number;
  // The domain behind the checkout; the paid promo apply/display uses it to
  // translate between the typed code and its namespaced Stripe-side string.
  domainId?: number;
  // The mount-time started checkout session / trial SetupIntent. Exactly one
  // is set once the anonymous start lands; until then the screen shows the
  // skeleton. Both are deferred-identity starts (no email at start).
  checkoutData?: CheckoutResponse | null;
  trialData?: TrialStartResponse | null;
  trialPeriodDays?: number;
  // The mount-time start is in flight (or hasn't fired): show the skeleton.
  starting?: boolean;
  // Paid/trial completion. Carries the backend completion, whose email the
  // success screen's account offer reads (the deferred-identity paths never
  // held the email widget-side), plus the display context the success screen
  // renders (the paid path's final session, the trial path's applied promo).
  onSuccess: (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => void;
  // Fired when a wallet confirm begins — the flow records the deferred-identity
  // path's identity_method_selected: wallet off it.
  onWalletConfirm?: (walletType: string) => void;
  // Fired when the card pay-button submit begins — the flow records the
  // deferred-identity path's identity_method_selected: guest off it,
  // symmetric with onWalletConfirm.
  onCardSubmit?: () => void;
  // Fired once when the Express Checkout Element reports ready; the flow
  // beacons it as the wallets_ready timing marker.
  onWalletsReady?: () => void;
  // False hides every wallet surface (see CheckoutFlow walletsEnabled).
  walletsEnabled?: boolean;
  // Trial-only callbacks, wired by CheckoutFlow (the single checkout
  // orchestrator — openCheckout and showPaywall both mount it).
  onTrialConfirm?: (setupIntentId: string, promoCode: string | null, email?: string) => Promise<CheckoutCompletionResponse>;
  onTrialIneligible?: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  onSetupConfirmStart?: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  onSetupConfirmSettled?: (setupIntentId: string) => void;
  onError: (error: CheckoutError) => void;
  onPromoChange?: (code: string | null) => void;
  // The mount's campaign code, auto-applied by the form once the start lands.
  campaignPromo?: CampaignPromo | null;
  // Fired when the campaign code's auto-apply succeeds.
  onCampaignApply?: () => void;
  // Fired when the campaign code's auto-apply fails, with the machine reason
  // and, when EWCS rejected the apply, Stripe's raw error text as detail.
  onCampaignApplyFailed?: (reason: string, detail?: string) => void;
  onLogin: () => void;
  // Header back arrow, wired by CheckoutFlow (returns to the plan picker).
  onBack?: () => void;
  onDismiss?: () => void;
}

// ExpeditedCheckoutStep is the single-screen guest checkout: an anonymous
// visitor on an expedited domain lands here straight from the plan picker and
// completes email + payment on one screen. The screen starts the checkout at
// mount (anonymously — the deferred-identity start), so the Stripe elements
// render immediately: wallet buttons (Express Checkout Element) on top, then
// Stripe's own email element (Contact Details Element for paid, Link
// Authentication Element for trials), then the Payment Element. Identity is
// adopted server-side at completion from the session/wallet email.
export function ExpeditedCheckoutStep({ product, price, amountCentsOverride, domainId, checkoutData, trialData, trialPeriodDays, starting, onSuccess, onWalletConfirm, onCardSubmit, onWalletsReady, walletsEnabled, onTrialConfirm, onTrialIneligible, onSetupConfirmStart, onSetupConfirmSettled, onError, onPromoChange, campaignPromo, onCampaignApply, onCampaignApplyFailed, onLogin, onBack, onDismiss }: Props) {
  useT();
  // The trial form's applied coupon, previewed in the order summary (a trial
  // has no live session to recompute totals from).
  const [trialDiscount, setTrialDiscount] = useState<TrialDiscount | null>(null);
  // The live session, fed up by PaymentForm so the order summary renders
  // Stripe-computed totals (tax, Adaptive Pricing) once the form mounts.
  const [session, setSession] = useState<StripeCheckoutSession | null>(null);
  // Disables the header back arrow while a confirm is in flight, same as the
  // standalone payment steps — backing out mid-confirm would tear down the
  // selection the confirm is about to complete against.
  const [submitting, setSubmitting] = useState(false);

  // The promo entry's sidebar mount point, handed to whichever form mounts
  // (see PaymentForm.promoTarget): the entry belongs with the order summary
  // whose totals it changes, not inside the card form.
  const [promoTarget, setPromoTarget] = useState<HTMLDivElement | null>(null);

  const trial = !!trialData;
  // The form mounts once the mount-time start lands; before that (or while it
  // is still in flight) the skeleton holds the space.
  const formReady = !starting && (!!checkoutData || !!trialData);

  const subtitle = trial ? trialSubtitle(trialPeriodDays) : t('expeditedCheckout.subtitle');

  return (
    <CheckoutShell
      ariaLabel={t('expeditedCheckout.title')}
      currentStep={1}
      sidebar={(
        <>
          <OrderSummary product={product} price={price} summary={session} amountCentsOverride={amountCentsOverride} trialPeriodDays={trialPeriodDays} discount={trialDiscount} />
          <div ref={setPromoTarget} />
        </>
      )}
      wide
      hideSteps
      title={<h2 class="sm-title">{trial ? t('expeditedCheckout.title.trial') : t('expeditedCheckout.title')}</h2>}
      onBack={onBack}
      backDisabled={submitting}
      onDismiss={onDismiss}
    >
      <p class="sm-subtitle">{subtitle}</p>

      {/* The login offer is for the anonymous visitors this screen serves;
          an identified visitor never lands here (a logged-in selection skips
          to the standalone payment step). */}
      {formReady && (
        <p class="sm-expedited-login">
          {t('expeditedCheckout.haveAccount')}{' '}
          <button type="button" class="sm-identity-change" onClick={onLogin}>
            {t('expeditedCheckout.login')}
          </button>
        </p>
      )}

      {!formReady && (
        <div class="sm-form-skeleton" aria-hidden="true">
          <div class="sm-form-skeleton-field" />
          <div class="sm-form-skeleton-field" />
          <div class="sm-form-skeleton-button" />
        </div>
      )}

      {formReady && trialData && (
        <TrialPaymentForm
          product={product}
          price={price}
          clientSecret={trialData.clientSecret}
          publishableKey={trialData.publishableKey}
          connectedAccount={trialData.providerAccount}
          promotionCodesEnabled={trialData.promotionCodesEnabled}
          promoTarget={promoTarget}
          deferredIdentity
          walletsEnabled={walletsEnabled}
          onConfirm={(setupIntentId, promoCode, confirmEmail) =>
            onTrialConfirm?.(setupIntentId, promoCode, confirmEmail) ?? Promise.reject(new Error(t('expeditedCheckout.sessionInvalid')))
          }
          onPromoChange={onPromoChange}
          campaignPromo={campaignPromo}
          onCampaignApply={onCampaignApply}
          onDiscountChange={setTrialDiscount}
          onWalletConfirm={onWalletConfirm}
          onCardSubmit={onCardSubmit}
          onWalletsReady={onWalletsReady}
          onSuccess={onSuccess}
          onTrialIneligible={(setupIntentId, promoCode, discount) => onTrialIneligible?.(setupIntentId, promoCode, discount)}
          onSetupConfirmStart={onSetupConfirmStart}
          onSetupConfirmSettled={onSetupConfirmSettled}
          onSubmittingChange={setSubmitting}
          onError={onError}
        />
      )}

      {formReady && !trial && checkoutData && (
        <PaymentForm
          clientSecret={checkoutData.clientSecret}
          publishableKey={checkoutData.publishableKey}
          checkoutId={checkoutData.checkoutId}
          connectedAccount={checkoutData.providerAccount}
          product={product}
          price={price}
          domainId={domainId}
          promotionCodesEnabled={checkoutData.promotionCodesEnabled}
          savePaymentMethod={checkoutData.savePaymentMethod}
          promoTarget={promoTarget}
          deferredIdentity
          walletsEnabled={walletsEnabled}
          onWalletConfirm={onWalletConfirm}
          onCardSubmit={onCardSubmit}
          onWalletsReady={onWalletsReady}
          onSuccess={onSuccess}
          onError={onError}
          onPromoChange={onPromoChange}
          campaignPromo={campaignPromo}
          onCampaignApply={onCampaignApply}
          onCampaignApplyFailed={onCampaignApplyFailed}
          onSessionChange={setSession}
          onSubmittingChange={setSubmitting}
        />
      )}

      <div class="sm-secure" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {t('common.securedByStripe')}
      </div>
    </CheckoutShell>
  );
}
